import { useCallback, useMemo, useState } from 'react';

import type { ApiError } from '../../../lib/errors';
import { useToast } from '../../../lib/toast';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { bienHieuLuc, kiemGiaTri, thanGhi, tinhThayDoi } from '../nghiep-vu/cai-dat';
import type { LoiGiaTri, ThayDoiNhap } from '../nghiep-vu/cai-dat';
import { khoaCuaPointer, loiTruongCua } from '../types';
import type {
  BanNhap,
  GhiCaiDatVao,
  LoiTruong,
  MucCaiDat,
  SecurityPolicyView,
  SettingDefinition,
} from '../types';
import { useChinhSach, useDanhMucCaiDat, useGhiChinhSach } from './useBaoMat';
import type { NguonChinhSach } from './useBaoMat';

/** Lỗi của một dòng: mã của phép kiểm phía giao diện, hoặc câu đã dịch từ backend. */
export type LoiDong = { kieu: 'giao-dien'; loi: LoiGiaTri } | { kieu: 'may-chu'; cau: string };

export interface FormCaiDat {
  nguon: NguonChinhSach;
  policy: SecurityPolicyView | undefined;
  danhMuc: Map<string, SettingDefinition>;
  dangTai: boolean;
  loiTai: unknown;
  taiLai: () => void;
  dangTaiLai: boolean;

  nhap: BanNhap;
  doi: (key: string, giaTri: unknown) => void;
  veMacDinh: (key: string) => void;
  boNhap: (key?: string) => void;

  thayDoi: ThayDoiNhap[];
  loi: Record<string, LoiDong>;
  /** Người sửa chỉ được siết (Tenant Admin ở tầng tenant). */
  chiSiet: boolean;
  /** Không có quyền sửa: mọi dòng chỉ xem, không có thanh lưu. */
  chiDoc: boolean;

  luu: (them: Omit<GhiCaiDatVao, 'settings'>) => Promise<boolean>;
  dangLuu: boolean;
  /** `VERSION_MISMATCH`: có người vừa lưu trước. */
  xungDot: boolean;
  /** Lỗi ghi không gắn với trường nào (mạng, quyền…). */
  loiChung: string | undefined;
  /**
   * Lỗi theo trường thô của lần ghi vừa rồi — cho ô cần trỏ sâu hơn một khoá
   * (`/settings/audit.alert_rules/2/threshold` hiện cạnh đúng quy tắc).
   */
  loiMayChu: LoiTruong[];
}

/**
 * Trạng thái của một form cài đặt dựng từ danh mục (ADR-0059 mục 8).
 *
 * Gọi **ở trên** hàng thẻ: `TabsTruot` chỉ dựng thẻ đang mở, nên giữ bản nháp
 * trong thẻ là đổi thẻ thì mất hết phần đang sửa mà không ai báo.
 *
 * Bản nháp sống qua lần tải lại sau `VERSION_MISMATCH` — người dùng thấy giá trị
 * mới của người kia mà phần mình đang sửa vẫn còn, rồi tự quyết lưu lại.
 */
export function useFormCaiDat(nguon: NguonChinhSach, tuyChon: { chiSiet?: boolean; chiDoc?: boolean; bat?: boolean } = {}): FormCaiDat {
  const { t } = useT();
  const dichLoi = useLoi();
  const toast = useToast();
  const qDanhMuc = useDanhMucCaiDat();
  const qPolicy = useChinhSach(nguon, tuyChon.bat ?? true);
  const ghi = useGhiChinhSach(nguon);
  const [nhap, datNhap] = useState<BanNhap>({});

  const policy = qPolicy.data;
  const danhMuc = useMemo(
    () => new Map((qDanhMuc.data?.settings ?? []).map((d) => [d.key, d])),
    [qDanhMuc.data],
  );
  const chiSiet = tuyChon.chiSiet ?? false;

  const thayDoi = useMemo(
    () => (policy ? tinhThayDoi(policy.settings, nhap, policy.scope) : []),
    [policy, nhap],
  );

  const apiLoi = ghi.error as ApiError | null;
  const xungDot = apiLoi?.code === 'VERSION_MISMATCH';

  const loi = useMemo(() => {
    const ra: Record<string, LoiDong> = {};
    if (!policy) return ra;
    const bayGio = Date.now();
    for (const key of Object.keys(nhap)) {
      const giaTri = nhap[key];
      const def = danhMuc.get(key);
      const m: MucCaiDat | undefined = policy.settings.find((x) => x.key === key);
      if (!def || !m || giaTri === null) continue;
      const kq = kiemGiaTri(def, m.bounds ?? bienHieuLuc(def, policy.plan_code), giaTri, {
        bayGio,
        keThua: m.inherited_value,
        chiSiet,
      });
      if (kq) ra[key] = { kieu: 'giao-dien', loi: kq };
    }
    // Lỗi theo trường của backend: chỉ hiện ở dòng chưa có lỗi phía giao diện.
    const chiTiet = loiTruongCua(apiLoi);
    for (const l of chiTiet) {
      const key = khoaCuaPointer(l.pointer);
      if (!key || ra[key]) continue;
      // `detail` là câu backend đã dịch theo Accept-Language: hiện nguyên văn.
      ra[key] = { kieu: 'may-chu', cau: l.detail };
    }
    return ra;
  }, [policy, nhap, danhMuc, chiSiet, apiLoi]);

  const loiMayChu = useMemo(() => (xungDot ? [] : loiTruongCua(apiLoi)), [apiLoi, xungDot]);
  const loiKhongGanTruong = loiMayChu
    .filter((l) => !khoaCuaPointer(l.pointer))
    .map((l) => l.detail);
  const coLoiTruong = Object.values(loi).some((l) => l.kieu === 'may-chu');
  const loiChung =
    !apiLoi || xungDot
      ? undefined
      : loiKhongGanTruong.length > 0
        ? loiKhongGanTruong.join(' ')
        : coLoiTruong
          ? undefined
          : dichLoi(apiLoi);

  const doi = useCallback(
    (key: string, giaTri: unknown) => {
      datNhap((n) => ({ ...n, [key]: giaTri }));
      if (ghi.error && !xungDot) ghi.reset();
    },
    [ghi, xungDot],
  );

  const veMacDinh = useCallback((key: string) => datNhap((n) => ({ ...n, [key]: null })), []);

  const boNhap = useCallback((key?: string) => {
    datNhap((n) => {
      if (key === undefined) return {};
      const { [key]: _bo, ...conLai } = n;
      return conLai;
    });
  }, []);

  const taiLai = useCallback(() => {
    ghi.reset();
    void qPolicy.refetch();
    void qDanhMuc.refetch();
  }, [ghi, qPolicy, qDanhMuc]);

  const luu = useCallback(
    async (them: Omit<GhiCaiDatVao, 'settings'>) => {
      if (!policy || thayDoi.length === 0) return false;
      try {
        await ghi.mutateAsync({
          ifMatch: policy.version,
          vao: { settings: thanGhi(thayDoi), ...them },
        });
        datNhap({});
        toast.success(t('caiDat.daLuu', { so: thayDoi.length }));
        return true;
      } catch {
        return false;
      }
    },
    [policy, thayDoi, ghi, toast, t],
  );

  return {
    nguon,
    policy,
    danhMuc,
    dangTai: qPolicy.isLoading || qDanhMuc.isLoading,
    loiTai: qPolicy.error ?? qDanhMuc.error,
    taiLai,
    dangTaiLai: qPolicy.isFetching,
    nhap,
    doi,
    veMacDinh,
    boNhap,
    thayDoi,
    loi,
    chiSiet,
    chiDoc: tuyChon.chiDoc ?? false,
    luu,
    dangLuu: ghi.isPending,
    xungDot,
    loiChung,
    loiMayChu,
  };
}
